import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionDrawer, { UI, btnSx } from "../components/ActionDrawer";
import { Empty, Notice, PageHead, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const dateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  })}, ${d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })}`;
};

const PAGE_SIZE = 15;

/* EO4. Launch events tied to an installation date. */
const Inaugurations = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [active, setActive] = useState(null);
  // `search` is what is typed; `searchTerm` is what is sent, a moment after typing stops.
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchRows = async (targetPage = page) => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchInaugurations(token, {
        page: targetPage,
        limit: PAGE_SIZE,
        search: searchTerm || undefined,
      });
      setRows(res.data || []);
      setSummary(res.summary || null);
      setPagination(res.pagination || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching inaugurations");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // A new search starts back at page 1.
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    fetchRows(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm]);

  const toItem = (row) => ({
    id: "EO4",
    screenKey: "assign-influencer",
    orderId: row.orderId,
    orderNumber: row.orderNumber,
    clinicName: row.clinicName,
    context: {
      city: row.city,
      inaugurationDate: dateTime(row.inaugurationDate),
      technicianName: row.technicianName,
      influencers: row.influencerOptions || [],
    },
  });

  const kpis = summary
    ? [
        { label: "Scheduled", value: summary.scheduled },
        { label: "Awaiting influencer", value: summary.awaitingInfluencer, alarm: true },
        { label: "This week", value: summary.thisWeek },
        { label: "Completed", value: summary.completed },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Inaugurations"
        lede="Launch events tied to an installation date."
        kpis={kpis}
      />

      <Box
        component="input"
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search clinic, city or guest"
        aria-label="Search inaugurations"
        sx={{
          display: "block",
          width: "100%",
          maxWidth: 420,
          marginBottom: "14px",
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "8px",
          bgcolor: UI.surface,
          color: UI.ink,
          fontSize: 13,
          padding: "8px 12px",
          fontFamily: "inherit",
          outline: "none",
          "&:focus": { borderColor: UI.p },
        }}
      />

      {loadFailed ? (
        <Empty
          title="Could not load inaugurations"
          sub={
            <Box
              component="button"
              type="button"
              onClick={fetchRows}
              sx={{ ...btnSx("ghost"), margin: "12px auto 0", display: "block" }}
            >
              Retry
            </Box>
          }
        />
      ) : rows.length === 0 && !isLoading ? (
        <Empty
          title={searchTerm ? "No inaugurations match your search" : "No inaugurations scheduled"}
          sub={searchTerm ? "Try a different clinic, city or guest." : "One appears when the Ops Lead sets an arrival date."}
        />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(268px, 1fr))",
            gap: "14px",
            marginBottom: "28px",
          }}
        >
          {rows.map((row) => (
            <Box
              key={row.orderId}
              sx={{
                bgcolor: UI.surface,
                border: "1px solid",
                borderColor: UI.line,
                borderRadius: "8px",
                padding: "13px 15px",
              }}
            >
              <Box
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "8px",
                  alignItems: "flex-start",
                  color: UI.ink,
                }}
              >
                <span>{row.clinicName}</span>
                {row.completed ? (
                  <Tag tone="g">Done</Tag>
                ) : row.influencer ? (
                  <Tag tone="bl">Scheduled</Tag>
                ) : (
                  <Tag tone="s">Needs influencer</Tag>
                )}
              </Box>
              <Box
                sx={{
                  fontSize: 12,
                  color: UI.ink3,
                  margin: "5px 0 11px",
                  lineHeight: 1.6,
                }}
              >
                {row.clinicName}
                <br />
                {row.city}
                <br />
                {dateTime(row.inaugurationDate)}
                <br />
                Guest: {row.influencer || "Not assigned"}
              </Box>
              <Box sx={{ display: "flex", gap: "7px" }}>
                {!row.completed && (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setActive(toItem(row))}
                    sx={btnSx("ghost")}
                  >
                    {row.influencer ? "Change influencer" : "Assign influencer"}
                  </Box>
                )}
                <Box
                  component="button"
                  type="button"
                  onClick={() => navigate(`/dashboard/orders/${row.orderId}`)}
                  sx={btnSx("ghost")}
                >
                  Open order
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <Notice>
        <b>Open decision.</b> The customer has no way to confirm or decline an
        inauguration date. Every event here is scheduled at them, not with them.
      </Notice>

      <ActionDrawer
        open={Boolean(active)}
        screenKey={active?.screenKey}
        item={active}
        onClose={() => setActive(null)}
        onSaved={() => {
          setActive(null);
          fetchRows();
        }}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default Inaugurations;
